import { PageTextNotesManager } from '../pdf-utils/page-text-notes-manager';
import { TextNoteElement, WordElement, PageElement } from './page-elements';
import { ITextIterator, ReadingAtom, OrderedXmlWord } from './interfaces';
import { Rectangle } from '../interfaces';
import { ReadingUnitType, TxtItemType } from './enums';
import { isRectContainsPoint, isRectIntersectRect } from '../common';
import { InfoTagDataItem } from '../pdf-utils/interfaces';
import { XmlDetailsHelper } from '../pdf-utils/xml-details.helper';
import { Core } from '@pdftron/webviewer';
import { isEnglishAbbreviation } from '../html-and-text-utils';


export class PdfTextIterator implements ITextIterator {
  private parsedData: XMLDocument;
  private xmlWords: PageElement[] = [];
  private orderedWords: OrderedXmlWord[] | null = null;
  private sentenceIdx: number = 0;
  private wordIdx: number = 0;

  private lastReturnedSentence: ReadingAtom[] | null = null;
  private altHiglightingWords: (Rectangle | null)[] = [];

  private isOrdrdReading: boolean = false;

  private get isOrderedReading(): boolean {
    return this.isOrdrdReading && this.orderedWords !== null;
  }

  private set isOrderedReading(value: boolean) {
    this.isOrdrdReading = value;
  }

  get altHighlightRects(): (Rectangle | null)[] {
    return this.altHiglightingWords.concat();
  }

  get isAlternativeReading(): boolean {
    return this.altHiglightingWords.length > 0;
  }

  get hasWordData(): boolean {
    return this.parsedData.getElementsByTagName('Word').length > 0;
  }

  constructor(public readonly xmlSource: string, public readonly pageIdx: number,
    dataType: 'text/xml' | 'application/xml', private readonly tagData: InfoTagDataItem | null = null,
    private pageTextNotesManager: PageTextNotesManager) {

    this.parsePageXml(xmlSource, dataType);

    let ignoreZones: Rectangle[] | undefined = this.mapIgnoreZones();
    let correctZones: Rectangle[] | undefined = this.mapOrderingZones();

    this.createWordsMap(ignoreZones, correctZones);
    this.addAnnotationsToWordsMap(pageIdx);

    this.createOrderedWordsMap();
  }

  isPageFinished(itemType: ReadingUnitType): boolean | undefined | number {
    if (itemType === ReadingUnitType.Word) {
      return this.wordIdx === this.xmlWords.length - 1;
    } else {
      if (this.isOrderedReading) {
        const orderedIdx = this.getOrderedWordIdxForXmlWord(this.wordIdx);

        if (!this.lastReturnedSentence && this.orderedWords) {
          return orderedIdx === this.orderedWords.length - 1;
        }

        if (this.lastReturnedSentence && this.orderedWords) {
          const lastWordIdx = this.lastReturnedSentence[this.lastReturnedSentence.length - 1].itemIdx;
          const lastSentenceWordOrderedIdx = this.getOrderedWordIdxForXmlWord(lastWordIdx);

          return orderedIdx === this.orderedWords.length - 1 || lastSentenceWordOrderedIdx === this.orderedWords.length - 1;
        }

      } else {
        if (!this.lastReturnedSentence) {
          return this.wordIdx === this.xmlWords.length;
        }

        return this.wordIdx === this.xmlWords.length - 1 || this.lastReturnedSentence !== null &&
          this.lastReturnedSentence.length > 0 &&
          this.lastReturnedSentence[this.lastReturnedSentence.length - 1].itemIdx === this.xmlWords.length - 1;
      }
    }
  }

  isPageStarted(itemType: ReadingUnitType): boolean | number {
    if (itemType === ReadingUnitType.Word) {
      return this.wordIdx <= 0;
    } else if (this.lastReturnedSentence !== null) {
      this.getFirstPartOfSentence(this.lastReturnedSentence);

      if (this.isOrderedReading) {
        const firstWordIdx = this.lastReturnedSentence[0].itemIdx;
        const firstWordOrederedIdx = this.getOrderedWordIdxForXmlWord(firstWordIdx);

        return this.lastReturnedSentence.length && firstWordOrederedIdx === 0;
      } else {
        return this.lastReturnedSentence.length && this.lastReturnedSentence[0].itemIdx === 0;
      }
    }

    return false;
  }

  addAnnotations(annotations: Core.Annotations.Annotation[]): void {
    annotations.forEach(annot => {
      this.pageTextNotesManager.addTextNote(this.xmlWords, annot);
    });

    if (this.orderedWords && this.orderedWords.length > 0 && this.tagData && this.tagData.Correct) {
      this.orderedWords = this.prepareOrderedXmlWords(this.tagData.Correct);
    }
  }

  deleteAnnotations(annotationIds: string[]): void {
    annotationIds.forEach(id => {
      const deletedWords = this.pageTextNotesManager.deleteTextNote(this.xmlWords, id);

      if (this.orderedWords && this.orderedWords.length > 0 && deletedWords && deletedWords.length > 0) {
        deletedWords.forEach(deletedWord => {
          if (this.orderedWords) {
            const deletedId = this.orderedWords.findIndex(x => x.word === deletedWord[0]);

            if (deletedId > 0) {
              this.orderedWords.splice(deletedId, 1);
            }
          }
        });
      }
    });
  }

  updateAnnotations(annotations: Core.Annotations.Annotation[]): void {
    this.deleteAnnotations(annotations.map(x => x.Id));
    this.addAnnotations(annotations);
  }

  getWordBeforeTextNote(annotationId: string): ReadingAtom | undefined {
    if (this.orderedWords && this.orderedWords.length > 0) {
      const annotIdx = this.orderedWords.findIndex(x => x.word instanceof TextNoteElement &&
        (x.word.element as Core.Annotations.Annotation).Id === annotationId);

      if (annotIdx > 0 && this.orderedWords[annotIdx]) {
        const prevWordIdx = annotIdx - 1;

        if (prevWordIdx >= 0 && this.orderedWords[prevWordIdx]) {
          const { word: { boundary, textContent } } = this.orderedWords[prevWordIdx];

          return {
            boundary,
            text: textContent,
            itemIdx: this.xmlWords.findIndex(x => {
              if (this.orderedWords) {
                return x === this.orderedWords[prevWordIdx].word;
              }
              return false;
            }),
          } as ReadingAtom;
        }
      }
    } else {
      const annotIdx = this.xmlWords.findIndex(x => x instanceof TextNoteElement &&
        (x.element as Core.Annotations.Annotation).Id === annotationId);

      if (annotIdx > 0 && this.xmlWords[annotIdx]) {
        const prevWordIdx = annotIdx - 1;

        if (prevWordIdx >= 0 && this.xmlWords[prevWordIdx]) {
          return {
            boundary: this.xmlWords[prevWordIdx].boundary,
            text: this.xmlWords[prevWordIdx].textContent,
            itemIdx: prevWordIdx,
          } as ReadingAtom;
        }
      }
    }
  }

  private prepareOrderedXmlWords(order: any): OrderedXmlWord[] {
    const result: OrderedXmlWord[] = [];

    this.xmlWords.forEach((word) => {
      const temp = {
        word,
        zone: -1
      };

      result.push(temp);
    });

    order.forEach((zone, index) => {
      result.forEach(item => {
        if (item.zone >= 0) {
          return;
        }

        const wordRect = item.word instanceof WordElement ? (item.word as WordElement).boundary :
          (item.word as TextNoteElement).boundary;

        if (isRectIntersectRect(wordRect, zone.BBox)) {
          item.zone = index;
        }
      });
    });
    return result.sort((a, b) => {
      if (a.zone === -1 && b.zone === -1) {
        return 0;
      } else if (a.zone === -1) {
        return 1;
      } else if (b.zone === -1) {
        return -1;
      }

      return a.zone - b.zone;
    });
  }

  getNextItem(itemType: ReadingUnitType): ReadingAtom[] | null | [null | ReadingAtom] {
    if (itemType === ReadingUnitType.Sentence) {
      return this.getNextSentence();
    } else if (itemType === ReadingUnitType.Word) {
      const result = this.goToNextWord();
      return Array.isArray(result) ? result : [result];
    }
    return null;
  }

  getPrevItem(itemType: ReadingUnitType): ReadingAtom[] | null | [null | ReadingAtom] {
    if (itemType === ReadingUnitType.Sentence) {
      return this.getPrevSentence();
    } else if (itemType === ReadingUnitType.Word) {
      const result = this.goToPrevWord();
      return Array.isArray(result) ? result : [result];
    }
    return null;
  }

  getFirstItem(itemType: ReadingUnitType): ReadingAtom[] | null | [null | ReadingAtom] {
    this.wordIdx = 0;

    if (itemType === ReadingUnitType.Sentence) {
      return this.getNextSentence();
    } else if (itemType === ReadingUnitType.Word) {
      return [this.getWordByIndex(this.wordIdx)];
    }
    return null;
  }

  getLastItem(itemType: ReadingUnitType): ReadingAtom[] | null | [null | ReadingAtom] {
    this.wordIdx = this.xmlWords.length - 1;

    if (itemType === ReadingUnitType.Sentence) {
      return this.getPrevSentence();
    } else if (itemType === ReadingUnitType.Word) {
      return [this.getWordByIndex(this.wordIdx)];
    }
    return null;
  }

  getWordsInRectangle(rect: Rectangle, checkQualityOfIntersection: boolean = false, startSymbPos: number = 0): ReadingAtom[] {
    this.lastReturnedSentence = null;
    this.isOrderedReading = false;

    // to avoid quad intersects a bit next line. ASG-1075
    const highPercantageCovered = (r1: Rectangle | null, r2: Rectangle) => {
      if (!r1) {
        return false;
      }
      const result = r1.y + r1.height - r2.y;
      return result >= r1.height * 0.8 && result <= r1.height * 1.2;
    };

    const wordItems: ReadingAtom[] = [];

    this.xmlWords.forEach((word, index) => {
      if (isRectIntersectRect(word.boundary, rect) &&
        (checkQualityOfIntersection ? highPercantageCovered(word.boundary, rect) : true)) {

        const wordItem: ReadingAtom = {
          symbPos: 0,
          itemIdx: index,
          sntncIndex: -1,
          itemType: TxtItemType.Word,
          isLast: false,
          lang: '',
          text: (word as WordElement).textContent || '',
          boundary: (word as WordElement).boundary,
          pageIdx: this.pageIdx,
        };

        wordItems.push(wordItem);
      }
    });

    if (wordItems.length > 0) {
      wordItems[wordItems.length - 1].isLast = true;
    }

    let symbPos = startSymbPos;
    wordItems.forEach(x => {
      x.symbPos = symbPos;
      symbPos += x.text.length + 1;
    });

    return wordItems;
  }

  getWordByPoint(x1: number, y1: number): ReadingAtom | null {
    if (!this.hasWordData) {
      return null;
    }

    this.lastReturnedSentence = null;

    this.isOrderedReading = false;

    const wordCoords = this.getAllWordsCoords();
    if (wordCoords.length) {
      for (let i = 0; i <= wordCoords.length; i++) {
        if (wordCoords[i]) {
          if (isRectContainsPoint(x1, y1, wordCoords[i])) {
            if (this.xmlWords[i]) {
              if (i < this.xmlWords.length && i >= 0) {
                const wordElement = this.xmlWords[i];
                if (wordElement instanceof WordElement && wordElement.isIgnored) {
                  return null;
                }
              }

              return {
                symbPos: 0,
                itemIdx: i,
                sntncIndex: -1,
                itemType: TxtItemType.Word,
                // isLast: (this.wordIdx >= this.xmlWords.length - 1),
                // ASG-1158 for fire stopOnCurrentItem in reading service,
                // always return last word on hover reading mode
                isLast: true,
                lang: '',
                text: (this.xmlWords[i] as WordElement).textContent || '',
                boundary: (this.xmlWords[i] as WordElement).boundary,
                // itemType: ReadingUnitType.Word, itemIdx: i,
                // boundary: (this.xmlWords[i] as WordElement).boundary, lang: '',
                // text: (this.xmlWords[i] as WordElement).textContent, isLast: (this.wordIdx >= this.xmlWords.length - 1),
                pageIdx: this.pageIdx,
              };
            }
          }
        }
      }
    }
    return null;
  }

  getItemClosestToPoint(itemType: ReadingUnitType, x1: number, y1: number, isOrderedReading: boolean)
    : ReadingAtom[] | null | [null | ReadingAtom] {
    this.lastReturnedSentence = null;

    const wordCoords = this.getAllWordsCoords();

    for (let i = 0; i < wordCoords.length; i++) {
      if (isRectContainsPoint(x1, y1, wordCoords[i])) {
        this.wordIdx = i;
        let r: OrderedXmlWord | undefined;
        let b = false;
        if (this.orderedWords) {
          r = this.orderedWords.find(word => {
            if (word.word === this.xmlWords[this.wordIdx]) {
              return true;
            }
            return false;
          });
          if (r) {
            b = r.zone >= 0;
          }
        }
        this.isOrderedReading = isOrderedReading && this.orderedWords !== null && b;

        if (itemType === ReadingUnitType.Sentence) {
          const closestSentence = this.getNextSentence();
          if (closestSentence) {
            return closestSentence;
          }
        } else {
          const altReadingAtoms = this.tryToGetAlternateText(itemType);
          this.altHiglightingWords = altReadingAtoms !== null ? this.altHiglightingWords : [];

          return altReadingAtoms ? altReadingAtoms : [this.getWordByIndex(this.wordIdx)];
        }
      }
    }

    return null;
  }

  isItemTextNote(itemIdx: number): boolean {
    if (itemIdx >= this.xmlWords.length) {
      return false;
    }

    const item = this.xmlWords[itemIdx];

    return item instanceof TextNoteElement;
  }

  private getNextSentence(endWordIdx: number = -1): ReadingAtom[] | null {
    if (!this.hasWordData) {
      return null;
    }

    if (this.lastReturnedSentence !== null && this.lastReturnedSentence.length) {
      this.wordIdx = this.lastReturnedSentence[this.lastReturnedSentence.length - 1].itemIdx;

      this.wordIdx = this.isOrderedReading ? this.getXmlWordIdxForOrderedWord(this.getOrderedWordIdxForXmlWord(this.wordIdx) + 1) :
        this.wordIdx + 1;
    }

    let isLast = false;
    const sntncIndex = endWordIdx > 0 ? 0 : this.sentenceIdx++;
    const wordItems: ReadingAtom[] = [];

    // #region: reading alternate text of zone
    const altReadingAtoms = this.tryToGetAlternateText(ReadingUnitType.Sentence);
    this.altHiglightingWords = altReadingAtoms !== null ? this.altHiglightingWords : [];

    if (altReadingAtoms) {
      return altReadingAtoms;
    }
    // #endregion

    if (this.isOrderedReading && this.orderedWords) {
      let orderedWordIndex = this.getOrderedWordIdxForXmlWord(this.wordIdx);


      for (; orderedWordIndex < this.orderedWords.length && !isLast; orderedWordIndex++) {
        this.wordIdx = this.getXmlWordIdxForOrderedWord(orderedWordIndex);

        const orderedWord = this.orderedWords[orderedWordIndex].word;

        if (orderedWord instanceof WordElement && orderedWord.isIgnored) {
          continue;
        }

        isLast = this.isPunctuationEndSentence(orderedWord.textContent || '')
          || this.paragraphChanged(this.wordIdx)
        // || this.zoneChanged(orderedWordIndex, NavigationDirection.Next);

        let textContent = (orderedWord as WordElement).textContent || '';
        if (isLast && textContent.length > 1) {
          isLast = !isEnglishAbbreviation(textContent);
        }

        const wordItem = {
          symbPos: 0,
          itemIdx: this.wordIdx,
          sntncIndex,
          itemType: TxtItemType.Word,
          isLast,
          lang: '',
          text: textContent,
          boundary: (orderedWord as WordElement).boundary,
          pageIdx: this.pageIdx,
        };

        wordItems.push(wordItem);
      }

      this.wordIdx = orderedWordIndex < this.orderedWords.length ? this.getXmlWordIdxForOrderedWord(orderedWordIndex) :
        this.orderedWords.length - 1;

    } else {
      for (; this.wordIdx < this.xmlWords.length && !isLast &&
        (endWordIdx > 0 ? this.wordIdx <= endWordIdx : true);
        this.wordIdx++) {
        const xmlWord = this.xmlWords[this.wordIdx];

        if (xmlWord instanceof WordElement && xmlWord.isIgnored) {
          continue;
        }

        isLast = (endWordIdx > 0 ? false : this.isPunctuationEndSentence(xmlWord.textContent || ''))
          || this.paragraphChanged(this.wordIdx);

        let textContent = (xmlWord as WordElement).textContent || '';
        if (isLast && textContent.length > 1) {
          isLast = !isEnglishAbbreviation(textContent);
        }

        const wordItem = {
          symbPos: 0,
          itemIdx: this.wordIdx,
          sntncIndex,
          itemType: TxtItemType.Word,
          isLast,
          lang: '',
          text: textContent,
          boundary: (xmlWord as WordElement).boundary,
          pageIdx: this.pageIdx,
        };

        wordItems.push(wordItem);
      }

      // sentenceItem.isLast = this.wordIdx >= this.xmlWords.length;
      // this.wordIdx = sentenceItem.words.length ? sentenceItem.words[sentenceItem.words.length - 1].itemIdx + 1 : 0;
      this.wordIdx = wordItems.length ? wordItems[wordItems.length - 1].itemIdx + 1 : 0;
    }



    // this.lastReturnedSentence = sentenceItem;
    this.lastReturnedSentence = wordItems;

    let symbPos = 0;
    wordItems.forEach(x => {
      x.symbPos = symbPos;
      symbPos += x.text.length + 1;
    });

    return wordItems;
  }

  private getPrevSentence(): ReadingAtom[] | null {
    if (!this.hasWordData) {
      return null;
    }

    if (this.lastReturnedSentence !== null && this.lastReturnedSentence.length) {
      this.wordIdx = this.lastReturnedSentence[0].itemIdx;

      this.wordIdx = this.isOrderedReading ? this.getXmlWordIdxForOrderedWord(this.getOrderedWordIdxForXmlWord(this.wordIdx) - 1) :
        this.wordIdx - 1;
    }

    let isLast = false;
    let skip = false;
    const wordItems: ReadingAtom[] = [];
    // const sentenceItem: SentenceItem = { itemType: ReadingUnitType.Sentence, itemIdx: this.sentenceIdx--, words: wordItems, isLast };
    this.sentenceIdx--;

    // #region: reading alternate text of zone
    const altReadingAtoms = this.tryToGetAlternateText(ReadingUnitType.Sentence, true);
    this.altHiglightingWords = altReadingAtoms !== null ? this.altHiglightingWords : [];

    if (altReadingAtoms) {
      return altReadingAtoms;
    }
    // #endregion

    if (this.isOrderedReading && this.orderedWords) {
      let orderedWordIndex = this.getOrderedWordIdxForXmlWord(this.wordIdx);
      orderedWordIndex = orderedWordIndex < 0 ? this.orderedWords.findIndex(word => word.zone < 0) : orderedWordIndex;

      for (; orderedWordIndex >= 0; orderedWordIndex--) {
        this.wordIdx = this.getXmlWordIdxForOrderedWord(orderedWordIndex);

        const xmlWord = this.orderedWords[orderedWordIndex].word;
        if (xmlWord instanceof WordElement && xmlWord.isIgnored) {
          continue;
        }

        isLast = this.isPunctuationEndSentence(xmlWord.textContent || '')
          || this.wordIdx === this.xmlWords.length - 1
          || this.paragraphChanged(this.wordIdx)
        // || this.zoneChanged(orderedWordIndex, NavigationDirection.Previous);

        let textContent = (xmlWord as WordElement).textContent || '';
        if (isLast && textContent.length > 1) {
          isLast = !isEnglishAbbreviation(textContent);
        }

        if (isLast && !skip) {
          skip = true;
        } else if (isLast && skip) {
          break;
        }

        if (skip) {
          let itemIdx = -1;
          itemIdx = this.xmlWords.findIndex(x => {
            if (this.orderedWords) {
              return x === this.orderedWords[orderedWordIndex].word;
            }
            return false;
          });
          const wordItem = {
            symbPos: 0,
            itemIdx,
            sntncIndex: this.sentenceIdx,
            itemType: TxtItemType.Word,
            isLast: orderedWordIndex === this.xmlWords.length - 1,
            lang: '',
            text: textContent,
            boundary: (xmlWord as WordElement).boundary,
            pageIdx: this.pageIdx,
          };

          wordItems.unshift(wordItem);
        }
      }

      this.wordIdx = orderedWordIndex > -1 ? this.getXmlWordIdxForOrderedWord(orderedWordIndex) : 0;
    } else {
      for (; this.wordIdx >= 0; this.wordIdx--) {
        const xmlWord = this.xmlWords[this.wordIdx];
        if (xmlWord instanceof WordElement && xmlWord.isIgnored) {
          continue;
        }

        isLast = this.isPunctuationEndSentence(xmlWord.textContent || '') ||
          this.paragraphChanged(this.wordIdx) ||
          this.wordIdx === this.xmlWords.length - 1;

        let textContent = (xmlWord as WordElement).textContent || '';
        if (isLast && textContent.length > 1) {
          isLast = !isEnglishAbbreviation(textContent);
        }

        if (isLast && !skip) {
          skip = true;
        } else if (isLast && skip) {
          break;
        }

        if (skip) {
          const wordItem = {
            symbPos: 0,
            itemIdx: this.wordIdx,
            sntncIndex: this.sentenceIdx,
            itemType: TxtItemType.Word,
            isLast: this.wordIdx === this.xmlWords.length - 1,
            lang: '',
            text: textContent,
            boundary: (xmlWord as WordElement).boundary,
            pageIdx: this.pageIdx,
          };

          wordItems.unshift(wordItem);
        }
      }

      this.wordIdx = wordItems.length ? wordItems[wordItems.length - 1].itemIdx + 1 : 0;
    }

    this.lastReturnedSentence = wordItems;

    let symbPos = 0;
    wordItems.forEach(x => {
      x.symbPos = symbPos;
      symbPos += x.text.length + 1;
    });

    return wordItems;
  }

  // #region: reading alternate text of zone
  private tryToGetAlternateText(itemType: ReadingUnitType, isPrev: boolean = false): ReadingAtom[] | null {
    if (this.tagData && this.tagData.Correct) {
      const orderedWordIndex = this.getOrderedWordIdxForXmlWord(this.wordIdx);

      if (orderedWordIndex >= 0 && this.orderedWords) {
        const previousWordZone = this.orderedWords[orderedWordIndex].zone;

        if (this.tagData.Correct[previousWordZone] && this.tagData.Correct[previousWordZone].AltText) {
          const words = this.getZoneWithAltText(previousWordZone, itemType, isPrev);
          return words;
        }
      }
    }
    return null;
  }

  private getZoneWithAltText(zone: number, itemType: ReadingUnitType, isPrev: boolean): ReadingAtom[] {
    const altWordItems: ReadingAtom[] = [];
    const wordItems: ReadingAtom[] = [];
    let zoneWords: OrderedXmlWord[] = [];
    if (this.orderedWords) {
      zoneWords = this.orderedWords.filter(x => x.zone === zone);
    }
    zoneWords.forEach((zoneWord, idx) => {
      const wordItem: ReadingAtom = {
        symbPos: 0,
        itemIdx: this.xmlWords.findIndex(x => x === zoneWord.word),
        sntncIndex: 0,
        itemType: TxtItemType.Word,
        isLast: idx === zoneWords.length - 1,
        lang: '',
        text: zoneWord.word.textContent || '',
        boundary: zoneWord.word.boundary,
        pageIdx: this.pageIdx,
      };

      wordItems.push(wordItem);
    });

    this.altHiglightingWords = zoneWords.map(x => x.word.boundary);

    let altWords: string[] = [];

    if (this.tagData) {
      altWords = this.tagData.Correct[zone].AltText?.split(' ') || [];
    }

    altWords.forEach((altWord, idx) => {
      const wordItem: ReadingAtom = {
        symbPos: 0,
        itemIdx: -1,
        sntncIndex: 0,
        itemType: TxtItemType.Word,
        isLast: idx === altWords.length - 1,
        lang: '',
        text: altWord,
        boundary: null,
        pageIdx: this.pageIdx,
      };

      altWordItems.push(wordItem);
    });

    if (itemType === ReadingUnitType.Word) {
      if (isPrev) {
        this.wordIdx = wordItems.length ? wordItems[0].itemIdx : 0;
      } else {
        this.wordIdx = wordItems.length ? wordItems[wordItems.length - 1].itemIdx : 0;
      }
    } else {
      if (this.isOrderedReading) {
        if (isPrev) {
          if (zone === 0) {
            this.wordIdx = wordItems.length ? wordItems[0].itemIdx : 0;
          } else if (this.orderedWords) {
            const prevZone = zone - 1;
            const prevZoneWords = this.orderedWords.filter(x => x.zone === prevZone);

            if (prevZoneWords.length) {
              const lastWordFromPrevZone = prevZoneWords[prevZoneWords.length - 1];
              this.wordIdx = this.getXmlWordIdxForOrderedWord(this.orderedWords.findIndex(x => x === lastWordFromPrevZone));
            } else {
              this.wordIdx = wordItems.length ? wordItems[0].itemIdx : 0;
            }
          }
        } else if (this.orderedWords) {
          this.wordIdx = this.getXmlWordIdxForOrderedWord(this.orderedWords.findIndex(x => x.zone === zone + 1));
        }
      } else {
        if (isPrev) {
          this.wordIdx = wordItems.length ? wordItems[0].itemIdx - 1 : 0;
        } else {
          this.wordIdx = wordItems.length ? wordItems[wordItems.length - 1].itemIdx + 1 : 0;
        }
      }
    }

    this.lastReturnedSentence = null;

    let symbPos = 0;
    altWordItems.forEach(x => {
      x.symbPos = symbPos;
      symbPos += x.text.length + 1;
    });

    return altWordItems;
  }

  // #endregion

  private getFirstPartOfSentence(words: ReadingAtom[]): void {
    if (words && words.length > 0) {
      const idx = words[0].itemIdx;

      if (idx > 0) {
        let isLast = false;

        for (let i = idx - 1; i >= 0; i--) {
          const xmlWord = this.xmlWords[i];

          isLast = this.isPunctuationEndSentence(xmlWord.textContent || '')
            || this.paragraphChanged(i);

          let textContent = (xmlWord as WordElement).textContent || '';
          if (isLast && textContent.length > 1) {
            isLast = !isEnglishAbbreviation(textContent);
          }

          if (isLast) {
            break;
          }

          const wordItem = {
            symbPos: 0,
            itemIdx: i,
            sntncIndex: words[0].sntncIndex,
            itemType: TxtItemType.Word,
            isLast: i === this.xmlWords.length - 1,
            lang: '',
            text: textContent,
            boundary: (xmlWord as WordElement).boundary,
            pageIdx: this.pageIdx,
          };

          words.unshift(wordItem);
        }

        let symbPos = 0;
        words.forEach(x => {
          x.symbPos = symbPos;
          symbPos += x.text.length + 1;
        });
      }
    }
  }

  private goToNextWord(): ReadingAtom | ReadingAtom[] | null {
    let wordElement: WordElement | null = null;

    do {
      if (this.isOrderedReading && this.orderedWords) {
        const orderedWordIndex = this.getOrderedWordIdxForXmlWord(this.wordIdx);

        this.wordIdx = this.orderedWords[orderedWordIndex].zone >= 0 ?
          this.getXmlWordIdxForOrderedWord(orderedWordIndex + 1) : this.wordIdx + 1;
      } else {
        this.wordIdx = this.wordIdx + 1;
      }

      if (this.wordIdx < 0 && this.wordIdx >= this.xmlWords.length) {
        this.wordIdx = -1;
        break;
      }

      wordElement = this.xmlWords[this.wordIdx] as WordElement;
    } while (wordElement.isIgnored);

    // #region: reading alternate text of zone
    const altReadingAtoms = this.tryToGetAlternateText(ReadingUnitType.Word);
    this.altHiglightingWords = altReadingAtoms !== null ? this.altHiglightingWords : [];

    return altReadingAtoms ? altReadingAtoms : this.getWordByIndex(this.wordIdx);
    // #endregion
  }

  private goToPrevWord(): ReadingAtom | ReadingAtom[] | null {
    let wordElement: WordElement | null = null;

    do {
      if (this.isOrderedReading && this.orderedWords) {
        const orderedWordIndex = this.getOrderedWordIdxForXmlWord(this.wordIdx);

        this.wordIdx = orderedWordIndex - 1 >= 0 ? this.orderedWords[orderedWordIndex].zone >= 0 ?
          this.getXmlWordIdxForOrderedWord(orderedWordIndex - 1) : this.wordIdx - 1 : 0;
      } else {
        this.wordIdx = (this.wordIdx - 1 >= 0) ? this.wordIdx - 1 : 0;
      }

      if (this.wordIdx < 0 && this.wordIdx >= this.xmlWords.length) {
        this.wordIdx = -1;
        break;
      }

      wordElement = this.xmlWords[this.wordIdx] as WordElement;
    } while (wordElement.isIgnored);

    // #region: reading alternate text of zone
    const altReadingAtoms = this.tryToGetAlternateText(ReadingUnitType.Word, true);
    this.altHiglightingWords = altReadingAtoms !== null ? this.altHiglightingWords : [];

    return altReadingAtoms ? altReadingAtoms : this.getWordByIndex(this.wordIdx);
    // #endregion
  }

  private getWordByIndex(idx: number): ReadingAtom | null {
    if (!this.hasWordData || idx < 0) {
      return null;
    }

    this.lastReturnedSentence = null;

    const xmlWord = this.xmlWords[idx];

    if (xmlWord) {
      let isLast = false;

      if (this.isOrderedReading && this.orderedWords) {
        const orderedWordIndex = this.getOrderedWordIdxForXmlWord(idx);
        isLast = orderedWordIndex >= this.orderedWords.length ||
          this.orderedWords[orderedWordIndex + 1] && this.orderedWords[orderedWordIndex + 1].zone < 0;
      } else {
        isLast = idx >= this.xmlWords.length - 1;
      }

      return {
        symbPos: 0,
        itemIdx: idx,
        sntncIndex: -1,
        itemType: TxtItemType.Word,
        isLast,
        lang: '',
        text: (xmlWord as WordElement).textContent || '',
        boundary: (xmlWord as WordElement).boundary,
        pageIdx: this.pageIdx,
      };
    }
    return null;
  }

  private getAllWords(): HTMLCollection {
    return this.parsedData.getElementsByTagName('Word');
  }

  private getAllWordsCoords(): (Rectangle | null)[] {
    const wordCoords: (Rectangle | null)[] = [];
    for (const i in this.xmlWords) {
      if (Number(i) <= (this.xmlWords.length - 1)) {
        if (this.xmlWords[i]) {
          wordCoords.push((this.xmlWords[i] as WordElement).boundary);
        }
      }
    }
    return wordCoords;
  }

  private isPunctuationEndSentence(txt: string): boolean {
    return !!txt.match(/[.!?;](?=(?:\s+["”'’])?(?![a-z]))/g);
  }

  private paragraphChanged(idx: number): boolean {
    if (this.xmlWords[idx] instanceof TextNoteElement || this.xmlWords[idx + 1] instanceof TextNoteElement) {
      return false;
    }

    const currentPara = (this.xmlWords[idx]?.element as Element)?.closest('Para');
    const nextPara = (this.xmlWords[idx + 1]?.element as Element)?.closest('Para');

    return (idx < this.xmlWords.length - 1)
      && currentPara !== null
      && nextPara !== null
      && currentPara !== nextPara
      && this.isParagraphStylesDifferent(idx);
    // TODO: check distance between paras/lines
  }

  // private zoneChanged(orderedIndex: number, direction: NavigationDirection): boolean {
  //   if (!this.orderedWords) {
  //     return false;
  //   }

  //   const currentZone = this.orderedWords[orderedIndex].zone;
  //   let nextZone = currentZone;

  //   if (direction === NavigationDirection.Next) {
  //     nextZone = orderedIndex + 1 < this.orderedWords.length ?
  //       this.orderedWords[orderedIndex + 1].zone
  //       : this.orderedWords[orderedIndex].zone;
  //   } else if (direction === NavigationDirection.Previous) {
  //     nextZone = orderedIndex - 1 >= 0 ?
  //       this.orderedWords[orderedIndex - 1].zone
  //       : this.orderedWords[orderedIndex].zone;
  //   }

  //   if (currentZone !== nextZone) {
  //     const firtRect = this.tagData?.Correct[currentZone]?.BBox;
  //     const secondRect = this.tagData?.Correct[nextZone]?.BBox;
  //     if (firtRect && secondRect) {
  //       const distance = getDistance(firtRect.x, firtRect.y, firtRect.x + firtRect.width, firtRect.y + firtRect.height,
  //         secondRect.x, secondRect.y, secondRect.x + secondRect.width, secondRect.y + secondRect.height);

  //       // TODO: analyze distance beetwen lines and words of page. Find minimal distance and compare with it for zones
  //       return distance > 5;
  //     }
  //   }

  //   return false;
  // }

  private createOrderedWordsMap() {
    if (!this.tagData || !this.tagData.Correct || this.tagData.Correct.length === 0) {
      return;
    }

    this.orderedWords = this.prepareOrderedXmlWords(this.tagData.Correct);
  }

  private addAnnotationsToWordsMap(pageIdx: number) {
    const textNotes = this.pageTextNotesManager.getTextNotes(pageIdx);
    this.addAnnotations(textNotes);
  }

  private createWordsMap(ignoreZones: Rectangle[] | undefined, correctZones: Rectangle[] | undefined) {
    Array.from(this.getAllWords()).forEach(item => {
      const wordElement = new WordElement(item);
      this.xmlWords.push(wordElement);
      if (ignoreZones) {
        ignoreZones.forEach((rect) => {
          if (isRectIntersectRect(wordElement.boundary, rect)) {
            wordElement.isIgnored = true;
          }
        });
      }
      if (correctZones) {
        for (let rect of correctZones) {
          if (isRectIntersectRect(wordElement.boundary, rect)) {
            wordElement.isIgnored = false;
            break;
          }
          else {
            wordElement.isIgnored = true;
          }
        };
      }
    });

    this.xmlWords.sort((a, b) => {
      if (!a.boundary && !b.boundary) {
        return 0;
      } else if (!a.boundary) {
        return 1;
      } else if (!b.boundary) {
        return -1;
      } else if (a.boundary.y.toFixed(0) === b.boundary.y.toFixed(0)) {
        return +a.boundary.x.toFixed(1) - +b.boundary.x.toFixed(1)
      }

      return +b.boundary.y.toFixed(0) - +a.boundary.y.toFixed(0);
    });
  }

  private parsePageXml(xmlSource: string, dataType: 'text/xml' | 'application/xml') {
    const parser = new DOMParser();
    this.parsedData = parser.parseFromString(xmlSource, dataType);
  }

  private isParagraphStylesDifferent(idx: number): boolean {
    if (this.xmlWords[idx] instanceof TextNoteElement || this.xmlWords[idx + 1] instanceof TextNoteElement) {
      return false;
    }

    const currentLine = (this.xmlWords[idx].element as Element).closest('Line');
    const nextLine = (this.xmlWords[idx + 1].element as Element).closest('Line');

    return XmlDetailsHelper.isLinesStylesDifferent(currentLine, nextLine);
  }

  private getOrderedWordIdxForXmlWord(idx: number): number {
    if (idx < 0 || !this.orderedWords || !this.xmlWords || idx >= this.xmlWords.length) {
      return -1;
    }

    return this.orderedWords.findIndex(word => word.word === this.xmlWords[idx]);
  }

  private getXmlWordIdxForOrderedWord(idx: number): number {
    if (idx < 0 || !this.orderedWords || !this.xmlWords || idx >= this.orderedWords.length) {
      return -1;
    }
    return this.xmlWords.findIndex(x => {
      if (this.orderedWords) {
        return x === this.orderedWords[idx].word;
      }
      return false;
    });
  }

  public getReadingItemByItemIdx(itemIdx: number): ReadingAtom[] | null {
    this.wordIdx = itemIdx;
    return this.getNextSentence();
  }

  private mapOrderingZones(): Rectangle[] | undefined {
    if (!this.tagData || !this.tagData.Correct || this.tagData.Correct.length === 0) {
      return;
    }

    return this.tagData.Correct.map(c => ({
      height: c.BBox.height,
      width: c.BBox.width,
      x: c.BBox.x,
      y: c.BBox.y,
    }));
  }

  private mapIgnoreZones(): Rectangle[] | undefined {
    if (!this.tagData || !this.tagData.Ignore || this.tagData.Ignore.length === 0) {
      return;
    }

    return this.tagData.Ignore.map(i => ({
      x: i['0'],
      y: i['1'],
      width: i['2'] - i['0'],
      height: i['3'] - i['1'],
    }));
  }
}
